import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { useStore } from 'src/contexts/StoreContext';
import { useEffect, useRef } from 'react';
import { isWeb } from 'src/_shared/utils/isWeb';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { Illustration } from '../illustration/Illustration';
import {
  DYNAMIC_MODAL_VIEWS,
  SELECTED_MENU_ITEMS,
} from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { ICON_NAMES } from '../icon/icon.helper';
import braze from 'src/_shared/braze';
import { ILLUSTRATION_NAMES } from '../illustration/illustration.helper';
import { filesToPickedImages } from 'src/_shared/utils/filesToPickedImages';
import { TestIDs } from 'src/constants/testIDs.constants';
import { choosePhotosBannerTestID } from 'src/helpers/testIDs.helper';
import { PrimaryButton } from '../buttons/primary-button/PrimaryButton';
import { styles } from './ChoosePhotosBanner.styles';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { DRAWER_POSITIONS, DRAWER_VIEWS } from 'src/store/drawer/drawer.constants';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { TextIconButton } from '../buttons/text-icon-button/TextIconButton';
import { TEXT_ICON_BUTTON_SIZES, TEXT_ICON_BUTTON_TYPES } from '../buttons/text-icon-button/TextIconButton.constants';
import { SourcesEventType } from '../modals/dynamic-modals/modal-upload-multisource/ModalUploadMultisource.constants';
import { useTypedRoute } from 'src/editor-v3/hooks/useTypedRoute';
import { useTypedNavigation } from 'src/editor-v3/hooks/useTypedNavigation';
import { GROUP_CODES } from 'src/constants/code.constants';

export const ChoosePhotosBanner = observer(() => {
  const {
    toastStore,
    drawer: { openDrawer, closeDrawer },
    project: {
      activeProject,
      addProducts,
      addProductsV2,
      startAddingPhotosFlow,
      productConfigurationId,
      showUploadPhotosBanner,
      activeGroup,
      groupCode,
    },
    dynamicModal: { openModal, closeModal },
    i18n,
  } = useStore();
  const dropZoneRef = useRef(null);

  const route = useTypedRoute<ROUTE_NAMES.EditorNew>();
  const navigation = useTypedNavigation<ROUTE_NAMES.EditorNew>();
  const url = navigation.getState()?.routes[0].params as any;
  const { isMobile, isDesktop } = useResponsiveScreen();

  const newRoute = [ROUTE_NAMES.EditorNew].includes(route?.name as ROUTE_NAMES);

  /* TO-DO: REMOVE FROM HERE */
  useEffect(() => {
    if (newRoute && showUploadPhotosBanner) {
      const currentParams = route.params;
      navigation.navigate(ROUTE_NAMES.Upload, {
        ...currentParams,
      });
    }
  }, [isDesktop, showUploadPhotosBanner]);

  useEffect(() => {
    // Manage the drag and drop event only on the web
    if (isWeb()) {
      const dropZone = dropZoneRef.current as any;

      dropZone?.addEventListener('dragover', (event: React.DragEvent) => {
        event.preventDefault();
      });

      dropZone?.addEventListener('drop-target', (event: React.DragEvent) => {
        event.preventDefault();
      });

      dropZone?.addEventListener('drop', async (event: React.DragEvent) => {
        event.preventDefault();

        const droppedFiles: File[] = [...event.dataTransfer.files];
        filesToPickedImages(
          droppedFiles,
          {
            i18n,
            errorCallback: ({ errorMessage }) => {
              toastStore.showErrorToast({
                id: 'alert-uploading-photos-error-toast',
                message: errorMessage,
              });
            },
          },
          newRoute,
        ).then(async files => {
          if (newRoute) {
            files.forEach(file => (file.source = SELECTED_MENU_ITEMS.Computer));
            await activeProject?.addImages(files).then(() => {
              openDrawer({
                drawerPosition: DRAWER_POSITIONS.Left,
                drawerView: DRAWER_VIEWS.PhotoDrawer,
                withBackdrop: false,
                openPoint: 'upload_photo',
                uploadButtonCallback: () => {
                  activeGroup?.checkMinMaxProductsAndNotify();
                  addProductsV2({
                    ids: activeProject?.selectedThumbnails.map(t => t.originalImageId),
                    productConfigurationId: activeGroup?.currectProductConfigurationId || productConfigurationId,
                    groupCode: activeGroup?.code || groupCode,
                    groupId: activeGroup?.id,
                  }).then(() => {
                    activeProject?.deselectAllThumbnailImages();
                  });
                  closeDrawer();
                },
              });
            });
          } else {
            await addProducts({ files, isDragged: true, isFromPersonalize: true });
          }

          amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
            n_photo: files.length,
            source_type: SourcesEventType.Computer,
          });
        });
      });
    }
  }, []);

  const onPressCTA = async () => {
    openModal({ dynamicModalView: DYNAMIC_MODAL_VIEWS.ExitProject });
    amplitude.trackEvent(AnalyticsEvents.personalize.backToHomeClick);
  };

  const onPressChooseButton = async () => {
    if (newRoute) {
      const currentParams = route.params;
      navigation.navigate(ROUTE_NAMES.Upload, {
        ...currentParams,
      });
    } else {
      const defaultMenuItem = SELECTED_MENU_ITEMS.Computer;
      openModal({
        selectedMenuItem: isMobile ? SELECTED_MENU_ITEMS.Gallery : defaultMenuItem,
        dynamicModalView: DYNAMIC_MODAL_VIEWS.UploadMultiSource,
        uploadMultiSourceCallback: addProducts,
        newUploadMultiSourceCallback: params => {
          closeModal();
          startAddingPhotosFlow({
            initialProductConfigurationId: url['productConfigurationId'] ?? productConfigurationId,
            groupCode: url['groupCode'] ?? GROUP_CODES.PRINT_CLASSIC,
            skipConfigurationSteps: (url['skipConfigurationSteps'] ?? '').split('_'),
            ...params,
          });
        },
      });
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.view, { type: 'manual' });
      braze.trackEvent(AnalyticsEvents.dynamicPicker.view);
    }
  };

  return (
    <View
      testID={choosePhotosBannerTestID()}
      style={newRoute ? styles.containerNew : styles.container}
      ref={dropZoneRef}
    >
      <View style={[isMobile ? styles.content_mobile : styles.content, isMobile && { maxWidth: 316 }]}>
        <View style={{ width: 100, height: 100 }}>
          <Illustration name={ILLUSTRATION_NAMES.AddPhoto} />
        </View>
        <TextLato style={isMobile ? styles.title_mobile : styles.title}>{i18n.t('customize:no-frame')}</TextLato>
        <View style={[styles.dropzone, !isMobile && styles.dropzoneArea]}>
          {!isMobile && (
            <TextLato style={[styles.dropzoneText]}>
              {i18n.t(newRoute ? 'customize:no-frame-description-new' : 'customize:no-frame-description')}
            </TextLato>
          )}
          <PrimaryButton
            style={isMobile ? styles.button_mobile : styles.button}
            onPress={onPressChooseButton}
            testID={choosePhotosBannerTestID(TestIDs.CHOOSE)}
            text={i18n.t('customize:choose')}
            textStyle={styles.buttonText}
            focusId={choosePhotosBannerTestID(TestIDs.CHOOSE)}
            focusOrder={0}
            focusGroup={FocusGroups.CHOOSE_PHOTO_BANNER}
          />
        </View>
        <View style={styles.otherOptionsContainer}>
          <TextLato style={[styles.otherOptionsText, isMobile && styles.otherOptionsTextMobile]}>
            {i18n.t('customize:create-something-else')}
          </TextLato>
          <TextIconButton
            onPress={onPressCTA}
            style={styles.goToSiteContainer}
            innerStyle={styles.goToSiteInnerContainer}
            focusStyle={styles.goToSiteContainerFocus}
            accessible={true}
            accessibilityLabel={i18n.t('customize:go-to-site')}
            accessibilityRole="button"
            text={i18n.t('customize:go-to-site')}
            textStyle={[styles.otherOptionsButton, isMobile && styles.otherOptionsTextMobile]}
            iconStyle={styles.icon}
            iconName={ICON_NAMES.ArrowRight1}
            size={TEXT_ICON_BUTTON_SIZES.SMALL}
            type={TEXT_ICON_BUTTON_TYPES.ONLY_CTA}
            testID={choosePhotosBannerTestID(TestIDs.CTA)}
            focusId={choosePhotosBannerTestID(TestIDs.CTA)}
            focusOrder={1}
            focusGroup={FocusGroups.CHOOSE_PHOTO_BANNER}
          />
        </View>
      </View>
    </View>
  );
});

ChoosePhotosBanner.displayName = 'ChoosePhotosBanner';
